import type { RegisterResponse } from "@novel/contracts";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";
import { Navigate,Link } from "react-router";
import { ApiError, login, register, verifyEmail } from "../../shared/api/client";
import { useSession } from "../../app/session";
import styles from "./LoginPage.module.css";

type Mode = "login" | "register";

export function LoginPage() {
  const { isAuthenticated, isLoading, isSigningOut } = useSession();
  const queryClient = useQueryClient();
  const [mode, setMode] = useState<Mode>("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [pendingVerification, setPendingVerification] = useState<{ url: string; token: string } | undefined>();
  const [notice, setNotice] = useState<string | undefined>();
  const [error, setError] = useState<string | undefined>();

  const authMutation = useMutation({
    mutationFn: async () => {
      if (mode === "login") return { kind: "login" as const, value: await login(email, password) };
      return { kind: "register" as const, value: await register(email, password) };
    },
    onSuccess: async (result) => {
      setError(undefined);
      if (result.kind === "login") {
        await queryClient.invalidateQueries({ queryKey: ["session"] });
        return;
      }
      const response = result.value as RegisterResponse;
      if (response.status === "active") {
        await login(email, password);
        await queryClient.invalidateQueries({ queryKey: ["session"] });
        setNotice("注册成功，正在进入书库。");
        return;
      }
      setNotice("注册成功。账号需要先完成邮箱验证才能登录。");
      if (response.verificationUrl) {
        const token = new URL(response.verificationUrl).searchParams.get("token") ?? "";
        setPendingVerification({ url: response.verificationUrl, token });
      }
    },
    onError: (thrown: unknown) => {
      setNotice(undefined);
      setError(thrown instanceof ApiError ? thrown.message : "请求失败，请稍后重试");
    }
  });

  const verifyMutation = useMutation({
    mutationFn: async (token: string) => verifyEmail(token),
    onSuccess: async () => {
      setError(undefined);
      setPendingVerification(undefined);
      setNotice("邮箱已验证，正在进入书库。");
      // 验证后直接用同一套凭据登录，避免让用户再输一次密码。
      await login(email, password);
      await queryClient.invalidateQueries({ queryKey: ["session"] });
    },
    onError: (thrown: unknown) => {
      setError(thrown instanceof ApiError ? thrown.message : "验证失败，请重新注册");
    }
  });

  if (isLoading) return <p className={styles.loading}>正在检查会话…</p>;
  if (isAuthenticated) return <Navigate to="/" replace />;

  function onSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    if(isSigningOut)return;
    setError(undefined);
    authMutation.mutate();
  }

  return (
    <main className={styles.shell}>
      <h1 className={styles.title}>小说写作</h1>
      <p className={styles.subtitle}>
        {mode === "login" ? "使用账号或邮箱登录。" : "输入邮箱和密码即可注册。"}
      </p>

      {mode==="login"&&<Link to="/forgot-password">忘记密码</Link>}
      <div className={styles.tabs} role="tablist">
        <button
          type="button"
          role="tab"
          aria-selected={mode === "login"}
          className={mode === "login" ? styles.tabActive : styles.tab}
          onClick={() => {
            setMode("login");
            setError(undefined);
            setNotice(undefined);
          }}
        >
          登录
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={mode === "register"}
          className={mode === "register" ? styles.tabActive : styles.tab}
          onClick={() => {
            setMode("register");
            setError(undefined);
            setNotice(undefined);
          }}
        >
          注册
        </button>
      </div>

      <form className={styles.form} onSubmit={onSubmit}>
        <label className={styles.field}>
          <span className={styles.label}>{mode === "login" ? "账号或邮箱" : "邮箱"}</span>
          <input
            type={mode === "login" ? "text" : "email"}
            required
            autoComplete={mode === "login" ? "username" : "email"}
            autoCapitalize="none"
            spellCheck={false}
            value={email}
            onChange={(event) => setEmail(event.target.value)}
          />
        </label>

        <label className={styles.field}>
          <span className={styles.label}>密码</span>
          <input
            type="password"
            required
            minLength={mode === "register" ? 10 : undefined}
            autoComplete={mode === "login" ? "current-password" : "new-password"}
            value={password}
            onChange={(event) => setPassword(event.target.value)}
          />
          {mode === "register" ? <span className={styles.hint}>至少 10 个字符</span> : null}
        </label>

        <button type="submit" className={styles.submit} disabled={authMutation.isPending||isSigningOut}>
          {isSigningOut?"正在结束上次会话…":authMutation.isPending ? "处理中…" : mode === "login" ? "登录" : "注册"}
        </button>
      </form>

      {notice ? <p className={styles.notice}>{notice}</p> : null}
      {error ? <p className={styles.error}>{error}</p> : null}

      {pendingVerification ? (
        <button
          type="button"
          className={styles.submit}
          disabled={verifyMutation.isPending}
          onClick={() => verifyMutation.mutate(pendingVerification.token)}
        >
          {verifyMutation.isPending ? "验证中…" : "打开验证链接完成激活"}
        </button>
      ) : null}
    </main>
  );
}
